import { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext, useWatch } from 'react-hook-form';
import { getConsecutiveSeatingsForQuantity } from 'src/utils/saleUtils';
import { isSectionTypeGeneralAdmission } from 'src/utils/ticketUtils';
import { ListingDetails, Ticket } from 'src/WebApiController';

export type UseSeatAutofillParams = {
  tickets: Ticket[] | undefined;
  quantitySold: number | undefined | null;
  listing: Pick<ListingDetails, 'sectionType' | 'isSeatSaver'> | undefined;
  section: string | undefined | null;
  /**
   * Optional override for the GA / non-consecutive-seat allowance. When
   * omitted the hook derives it from `listing.sectionType` alone. The
   * single-sale flow passes its venue-map-derived value here so the extracted
   * hook preserves parity with the original inline logic.
   */
  canAllocateToNonConsecutiveSeats?: boolean;
  /**
   * Form field path to read/write for `seatFrom`. Defaults to `'seatFrom'`
   * so the single-sale flow can adopt the hook with no other changes. Nested
   * paths (e.g. `'perListingSelections.0.seatFrom'`) are supported by
   * react-hook-form's dot-path syntax; typed as string to allow arbitrary
   * array indices without leaking generics into every caller.
   */
  seatFromPath?: string;
  /** Form field path for `seatTo`. Defaults to `'seatTo'`. */
  seatToPath?: string;
  /** Form field path for `quantitySold`. Defaults to `'quantitySold'`. */
  quantitySoldPath?: string;
};

export type UseSeatAutofillResult = {
  maxAvailableQuantity: number;
  seatFromOptions: Record<string, string>;
  seatFromToOptions?: Record<number, number>;
};

/**
 * Shared seat-autofill logic extracted from RetailSaleBuyerStep.components.tsx
 * (POS-8649). Reacts to changes in `quantitySold` and recomputes:
 *   - the set of consecutive-seat windows available in the given tickets
 *   - the `seatFrom` and `seatTo` form fields to reflect the first valid window
 *
 * Uses `getConsecutiveSeatingsForQuantity` from saleUtils under the hood.
 *
 * Consumed by:
 *   - `RetailSaleBuyerStep.components.tsx#TicketSection` (single-listing flow,
 *     default paths — no change to that call site's behavior).
 *   - `MultiListingRetailSaleCheckoutStep.tsx#MultiListingTicketRow` (per-row
 *     autofill using nested paths).
 */
export const useSeatAutofill = ({
  tickets,
  quantitySold,
  listing,
  section,
  canAllocateToNonConsecutiveSeats: canAllocateToNonConsecutiveSeatsProp,
  seatFromPath = 'seatFrom',
  seatToPath = 'seatTo',
  quantitySoldPath = 'quantitySold',
}: UseSeatAutofillParams): UseSeatAutofillResult => {
  // Untyped form context on purpose — callers pass nested/dynamic paths that
  // don't match the top-level RetailSaleFormValues shape.
  const { control, setValue } = useFormContext();

  // useWatch (not watch) for reliable reactivity on nested useFieldArray
  // paths — plain watch() doesn't fire on setValue writes to array items.
  const seatFrom = useWatch({ control, name: seatFromPath }) as
    | string
    | null
    | undefined;

  const [seatFromToOptions, setSeatFromToOptions] = useState<
    Record<number, number> | undefined
  >();
  const [seatFromOptions, setSeatFromOptions] = useState<
    Record<string, string>
  >({});
  const [maxAvailableQuantity, setMaxAvailableQuantity] = useState<number>(
    tickets?.length ?? 0
  );

  const setSeatTo = (
    quantitySoldValue: number,
    seatFr: string,
    seatToOrdinal: number
  ) => {
    if (quantitySoldValue < 2) {
      setValue(seatToPath, seatFr);
    } else {
      const t = tickets!.find((t) => t.lstOrd === seatToOrdinal);
      if (t) setValue(seatToPath, t.seat);
    }
  };

  // Section-type-based non-consecutive-seat allowance. Callers that already
  // have a venue-map-derived answer (single-sale flow) should pass it as
  // `canAllocateToNonConsecutiveSeats`; otherwise we fall back to
  // `listing.sectionType` (loaded eagerly by useRetailSaleListingTickets). The
  // hook intentionally does NOT spawn a venue-map query itself — that would
  // multiply the network cost by N rows in the multi-listing table.
  const canAllocateToNonConsecutiveSeats =
    canAllocateToNonConsecutiveSeatsProp ??
    (listing?.sectionType != null
      ? isSectionTypeGeneralAdmission(listing.sectionType)
      : false);

  // Also re-run when `tickets` changes so that once ticket details have loaded
  // (the array flips from undefined to a real list), we correctly seed the
  // initial seatFrom/seatTo instead of leaving them null.
  //
  // The original hook only depended on `quantitySold`; we intentionally add
  // `tickets` to the dependency list because the multi-listing table opens
  // with the full ticket set already resolved (async load happened in the
  // bulk-action layer) and needs a first-render fill. The extra dep is safe
  // for the single-sale flow because tickets there are also loaded once,
  // synchronously, before this hook renders.
  //
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const result = getConsecutiveSeatingsForQuantity(
      tickets,
      quantitySold,
      canAllocateToNonConsecutiveSeats
    );
    if (!result || listing?.isSeatSaver) return;

    setMaxAvailableQuantity(result.maxAvailableQuantity);

    if (result.maxAvailableQuantity < (quantitySold ?? 0)) {
      // Requested qty exceeds what's available — clamp down and let the
      // next effect cycle recompute options for the clamped value.
      setValue(quantitySoldPath, result.maxAvailableQuantity);
      return;
    }

    const newSeatFromToOptions = result.result;

    if (
      tickets?.length &&
      newSeatFromToOptions &&
      !isEqual(newSeatFromToOptions, seatFromToOptions)
    ) {
      setSeatFromToOptions(newSeatFromToOptions);
      const seatFromOrdinals = Object.keys(newSeatFromToOptions);
      const newSeatFromOptions = seatFromOrdinals.reduce(
        (r, c) => {
          const t = tickets!.find((t) => t.lstOrd === parseInt(c));
          if (t) r[(t.lstOrd ?? t.ord).toString()] = t.seat;
          return r;
        },
        {} as Record<string, string>
      );

      if (!seatFrom) {
        const seatFr = Object.values(newSeatFromOptions)[0];
        if (seatFr != null) {
          setValue(seatFromPath, seatFr);
          const seatToOrdinal =
            newSeatFromToOptions[parseInt(seatFromOrdinals[0])];
          setSeatTo(quantitySold ?? 1, seatFr, seatToOrdinal);
        }
      } else {
        // Either the seatFr is no longer valid due to quantity change, or
        // seatTo needs revalidation — recompute both from the current seatFr
        // where possible, falling back to the first valid window.
        const seatFromTicket = tickets!.find((t) => t.seat === seatFrom);

        if (seatFromTicket) {
          let seatFr = seatFromTicket.seat;
          let seatFromOrdinal = seatFromTicket.lstOrd ?? seatFromTicket.ord;
          if (!seatFromOrdinals.includes(seatFromOrdinal.toString())) {
            seatFr = Object.values(newSeatFromOptions)[0];
            setValue(seatFromPath, seatFr);
            seatFromOrdinal = parseInt(seatFromOrdinals[0]);
          }

          const seatToOrdinal = newSeatFromToOptions[seatFromOrdinal];
          setSeatTo(quantitySold ?? 1, seatFr, seatToOrdinal);
        }
      }

      setSeatFromOptions(newSeatFromOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantitySold, tickets, section]);

  return {
    maxAvailableQuantity,
    seatFromOptions,
    seatFromToOptions,
  };
};
